import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { PasswordField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { acceptInvite } from '@/lib/members';
import { readApiError } from '@/lib/auth-api';

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <AuthLayout
        title={t('auth.invite.invalidTitle', { defaultValue: 'Invitation link missing' })}
        subtitle={t('auth.invite.invalidBody', { defaultValue: 'This link is missing its token. Ask your admin to resend the invite.' })}
      >
        <Link to="/login" className="gradient-primary inline-flex w-full h-11 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow">
          {t('auth.invite.backToLogin', { defaultValue: 'Back to sign in' })}
        </Link>
      </AuthLayout>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError(t('auth.errors.passwordMin', { defaultValue: 'Password must be at least 8 characters.' })); return; }
    setLoading(true);
    setError(null);
    try {
      await acceptInvite({ token, password, displayName: displayName.trim() || undefined });
      await qc.invalidateQueries({ queryKey: ['currentUser'] });
      nav('/app', { replace: true });
    } catch (err) {
      setError(readApiError(err, t('auth.invite.failed', { defaultValue: 'Could not accept the invitation.' })));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.invite.title', { defaultValue: 'Join your team' })}
      subtitle={t('auth.invite.subtitle', { defaultValue: 'Set a password to accept your KeenEZ invitation.' })}
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormError message={error} />
        <div>
          <label className="block text-sm font-medium mb-1.5">
            {t('auth.invite.name', { defaultValue: 'Your name' })}
            <span className="text-muted-foreground ms-2 font-normal">{t('common.optional', { defaultValue: 'optional' })}</span>
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            placeholder={t('auth.invite.namePlaceholder', { defaultValue: 'Jane Doe' })}
          />
        </div>
        <PasswordField
          label={t('auth.invite.password', { defaultValue: 'Choose a password' })}
          autoComplete="new-password"
          placeholder={t('auth.placeholders.password', { defaultValue: '••••••••' })}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          leadingIcon={<Lock className="h-4 w-4" />}
          showStrength
          required
        />
        <SubmitButton loading={loading}>{t('auth.invite.submit', { defaultValue: 'Accept & join' })}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
